<template>
  <a-form ref="formRef" :model="formState" :rules="rules" layout="vertical">
    <div>
      <a-form-item name="start">
        <div class="display-grid">
          <a-tooltip>
            <template #title v-if="tooltip">
              {{ tooltip }}
            </template>
            <a-time-picker
              class="picker__field"
              v-model:value="formState.start"
              style="width: 12em"
              placeholder="Hora de inicio"
              @openChange="handleStartOpenChange"
              format="HH:mm"
              valueFormat="HH:mm:ss"
              @change="handleChangePicker($event, 'start')"
              :disabled="disabled"
              :disabled-hours="filterDisabledHours"
              :disabled-minutes="() => filterDisabledMinutes(disabledMinutes, 'start')"
              :allowClear="false"
            />
          </a-tooltip>
          <!-- </a-form-item> -->
          <!-- <a-form-item name="end" class="picker__field"> -->
          <a-tooltip>
            <template #title v-if="tooltip">
              {{ tooltip }}
            </template>
            <a-time-picker
              class="picker__field"
              v-model:value="formState.end"
              :disabled="hourStart === null || disabled"
              style="width: 12em"
              placeholder="Hora de fin"
              format="HH:mm"
              valueFormat="HH:mm:ss"
              :open="endOpen"
              @openChange="handleEndOpenChange"
              @change="handleChangePicker($event, 'end')"
              :disabled-minutes="() => filterDisabledMinutes(disabledMinutes, 'end')"
              :disabled-hours="filterDisabledHours"
              :allowClear="false"
            />
          </a-tooltip>

          <a-button
            type="text"
            :disabled="disabled"
            style="padding: 0"
            v-if="showIconRemove && !isSequence"
            @click="$emit('onRemove', idPicker)"
          >
            <CloseOutlined
              class="picker__icon--remove"
            />
          </a-button>
          <a-button
            v-if="showIconRemove && isSequence"
            :disabled="disabled"
            style="padding: 0"
            @click="$emit('onRemove', idPicker)"
          >
            <DeleteOutlined
              class="picker__icon--remove"
            />
          </a-button>
        </div>
      </a-form-item>
    </div>

    <p class="error" v-if="isOverlap">Existe superposición de horarios</p>
  </a-form>
</template>

<script setup lang="ts">
import { ref, reactive, toRefs, nextTick, watch } from "vue";
import { CloseOutlined, DeleteOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import moment from "moment";

export interface Range {
  start: Time;
  end: Time;
}

export interface Time {
  hour: number;
  minutes: number;
}

interface Props {
  idPicker?: string;
  orderDay?: number;
  showIconRemove?: boolean;
  hourStart?: string;
  hourEnd?: string;
  disabledHours?: number[];
  disabledMinutes?: number[];
  disabled?: boolean;
  isSequence?: boolean;
  tooltip?: string;
}

const props = withDefaults(defineProps<Props>(), {
  orderDay: 0,
  showIconRemove: true,
  disabledHours: () => [],
  disabledMinutes: () => [],
  disabled: false,
  isSequence: false,
});

const { idPicker, orderDay, hourStart, hourEnd, disabledHours } = toRefs(props);

const formRef = ref();
const formState = reactive({
  start: hourStart?.value,
  end: hourEnd?.value,
});
const endOpen = ref(false);
const timeFormat = moment.HTML5_FMT.TIME_SECONDS;

// EVENTS
const emit = defineEmits(["onChangePicker", "onRemove"]);

const handleStartOpenChange = (open: boolean) => {
  if (!open) {
    endOpen.value = true;
  }
};

const handleEndOpenChange = (open: boolean) => {
  endOpen.value = open;
};

const handleChangePicker = (value: string, type: string) => {
  const range = { start: hourStart?.value, end: hourEnd?.value };
  if (type === "start") range.start = value;
  if (type === "end") range.end = value;

  emit("onChangePicker", idPicker?.value, range, orderDay.value, type);
};

// CALCULATIONS
const range = (start: number, end: number): number[] => {
  const result = [];
  for (let i = start; i <= end; i++) {
    result.push(i);
  }
  return result;
};

const filterDisabledHours = () => {
  const ownHours = range(
    moment(hourStart?.value, timeFormat).hours(),
    moment(hourEnd?.value, timeFormat).hours()
  );

  return disabledHours.value.filter((hour) => !ownHours.includes(hour));
};

const calculateOverlap = async () => {
  await nextTick();

  const ownHours = range(
    moment(formState.start, timeFormat).hours(),
    moment(formState.end, timeFormat).hours()
  );

  const validHours = ownHours.filter((hour) => !disabledHours.value.includes(hour));
  console.log({ ownHours, validHours, disabledHours: disabledHours.value });

  return validHours.length !== ownHours.length;
};

const filterDisabledMinutes = (disabledMinutes: number[], type: string) => {
  const initialRange: Range = {
    start: {
      hour: moment(hourStart?.value, timeFormat).hours(),
      minutes: moment(hourStart?.value, timeFormat).minutes(),
    },
    end: {
      hour: moment(hourEnd?.value, timeFormat).hours(),
      minutes: moment(hourEnd?.value, timeFormat).minutes(),
    },
  };

  const ownMinutes: number[] = [];
  if (initialRange.start.hour < initialRange.end.hour) {
    // si es start tomara del min q tenga hasta el min 59
    if (type === "start") ownMinutes.push(...range(initialRange.start.minutes, 59));
    else if (type === "end") ownMinutes.push(...range(0, initialRange.end.minutes));
  } else {
    ownMinutes.push(...range(initialRange.start.minutes, initialRange.end.minutes));
  }

  return disabledMinutes.filter((minute) => !ownMinutes.includes(minute));
};

// FORM VALIDATIONS
const isOverlap = ref(false);
const checkIsValidStart = async () => {
  if (!formState.end || !formState.start) return Promise.resolve();

  if (formState.start >= formState.end)
    return Promise.reject("La hora de inicio debe ser menor a la hora de fin");
};

// const checkIsValidRange = async (rule: Rule, value: string): Promise<void | string> => {
//   if (!formState.start) return;

//   if (formState.start < value) return Promise.resolve();
//   return Promise.reject("La hora de fin debe ser mayor a la hora de inicio");
// };

const checkHasOverlap = async () => {
  // if (rule.skipValidation) {
  //   return Promise.resolve();
  // }

  if (skipValidation.value) return;

  const hasOverlap = await calculateOverlap();

  // if (!hasOverlap) {
  //   isOverlap.value = false;
  //   return Promise.resolve();
  // } else {
  //   isOverlap.value = true;
  //   return Promise.reject("Existe superposición de horarios");
  // }
  isOverlap.value = hasOverlap;
};

const skipValidation = ref(false);

watch(formState, async () => {
  await checkHasOverlap();
});

const checkRequiredDates = async (): Promise<void | string> => {
  if (formState.start && formState.end) return Promise.resolve();
  return Promise.reject("Horario requerido");
};

const rules = ref({
  start: [
    // { required: true, message: "Campo requerido" },
    { validator: checkIsValidStart, trigger: "change" },
    { validator: checkRequiredDates, trigger: "change" },
    // { validator: checkHasOverlap, trigger: "change", skipValidation: false },
  ],
  // end: [
  //   { required: true, message: "Campo requerido" },
  // { validator: checkIsValidRange, trigger: "change" },
  // { validator: checkHasOverlap, trigger: "change", skipValidation: false },
  // ],
});

const validateRangePicker = async () => {
  try {
    skipValidation.value = true;
    if (isOverlap.value) {
      message.error("Por favor, corrige los horarios antes de guardar");
      throw new Error();
    }
    await formRef.value.validate();
  } catch (error) {
    console.log("error", error);
    return false;
  } finally {
    skipValidation.value = false;
  }

  return true;
};

const handleSubmit = async () => {
  try {
    const { err, values } = await formRef.value.validateFields();
    if (!err) {
      console.log(values);
    }
  } catch (error) {
    console.error(error);
  }
};
defineExpose({ validateRangePicker, handleSubmit });
</script>

<style lang="sass" scoped>
.range__icon
  font-size: 8px
  // margin: auto 8px
.picker__field
  width: 12em
  margin-bottom: 0px

  &:nth-child(1)::after
    content: "-"
    font-size: 24px
    color: #d9d9d9
    position: absolute
    top: 0
    left: 175px

.picker__icon--remove
  color: $red-6
  height: 24px
  width: 20px

.display-grid
  padding: 5px 0
  display: grid
  grid-template-columns: 190px 175px 20px
  align-items: center
  position: relative

.error
  color: #ff4d4f

:global(.ant-picker-time-panel)
  width: 160px !important
</style>
